<template>
	<div
		id="timelineView"
		:class="{ animate: animateView }"
		:style="{ 'animation-delay': animationDelay }"
		class="content-container carousel-view"
	>
		<section
			class="section-container carousel-section timeline-section"
			:class="{ animate: animate }"
		>
			<div
				ref="stage"
				class="tl-stage"
				tabindex="0"
				:style="{ '--toolbar-h': toolbarHeight + 'px' }"
				@keydown.left.prevent="step(-1)"
				@keydown.right.prevent="step(1)"
			>
				<FlickeringGrid class="tl-bg" />

				<!-- Horizontal track: stops 80px short of the bottom (the carousel roster's height),
				     so its axis sits level with the carousel arrows on Kavaliers / Chassis -->
				<div
					ref="track"
					class="tl-track"
					:class="{ dragging }"
					@wheel="onWheel"
					@scroll="onScroll"
					@pointerdown="onPointerDown"
				>
					<div class="tl-rail" @click.self="clearSelection">
						<div ref="axis" class="tl-axis"></div>
						<template v-for="item in laidOut" :key="item.uuid">
							<div v-if="item.marker" class="tl-marker" :data-uuid="item.uuid">
								<span class="tl-marker-label"
									>{{ item.year }}<small>{{ item.era }}</small></span
								>
							</div>
							<div
								v-else
								class="tl-event"
								:class="[item.side, { active: isSelected(item) }]"
								:data-uuid="item.uuid"
								:style="{
									'--c': getFactionColor(primaryFaction(item)),
									'--split': splitGradient(item),
								}"
								@click="clickEvent(item, $event)"
							>
								<div class="tl-card">
									<template v-if="isSelected(item)">
										<div class="tl-card-head">
											<span class="tl-card-year"
												>{{ item.year }}<small>{{ item.era }}</small></span
											>
											<div class="tl-card-factions">
												<span
													v-for="fk in sortedFactions(item.factions)"
													:key="fk"
													class="faction-tag"
													:style="{
														borderColor: getFactionColor(fk),
														color: readable(getFactionColor(fk)),
														background: getFactionColor(fk) + '26',
													}"
												>
													{{ getFactionName(fk) }}
												</span>
											</div>
										</div>
										<span class="tl-card-title">{{ item.title }}</span>
										<p v-if="item.descr" class="tl-card-full">{{ item.descr }}</p>
										<div v-if="validSources(item).length" class="tl-card-sources">
											<span v-for="(src, k) in validSources(item)" :key="k" class="source-entry">
												<a
													v-if="getSourceUrl(src.sourceKey) !== '#'"
													:href="getSourceUrl(src.sourceKey)"
													target="_blank"
													class="source-link"
													@click.stop
													>{{ getSourceName(src.sourceKey) }}</a
												>
												<span v-else class="source-name">{{ getSourceName(src.sourceKey) }}</span>
												<span v-if="src.sourceLocation" class="source-location">{{
													src.sourceLocation
												}}</span>
											</span>
										</div>
									</template>
									<template v-else>
										<span class="tl-card-year"
											>{{ item.year }}<small>{{ item.era }}</small></span
										>
										<span class="tl-card-title">{{ item.title }}</span>
										<span v-if="item.descr" class="tl-card-descr">{{ item.descr }}</span>
										<span class="tl-card-dots">
											<span
												v-for="fk in sortedFactions(item.factions)"
												:key="fk"
												class="faction-dot"
												:style="{ background: getFactionColor(fk) }"
												:title="getFactionName(fk)"
											></span>
										</span>
									</template>
								</div>
								<span class="tl-stem"></span>
								<span class="tl-node"></span>
							</div>
						</template>
						<div v-if="events.length === 0" class="tl-empty">// NO EVENTS MATCH FILTER //</div>
					</div>
				</div>

				<!-- Filters, floating over the top of the track -->
				<div ref="toolbar" class="tl-toolbar">
					<span class="tl-heading">THE ANNALS</span>
					<div class="filter-pills">
						<button
							v-for="era in ['ALL', 'BU', 'U']"
							:key="era"
							class="filter-pill"
							:class="{ active: eraFilter === era }"
							@click="eraFilter = era"
						>
							{{ era === "ALL" ? "All eras" : era === "BU" ? "Before Union" : "Union Era" }}
						</button>
					</div>
					<!-- The campaign is set in 5019u: the canon Dawnline Incident (5019-5021u) stays hidden unless asked for -->
					<button
						class="filter-pill tl-canon"
						:class="{ active: showCanon }"
						:aria-pressed="showCanon"
						title="Show the published events of the Dawnline Incident (5019–5021u). Your campaign may not follow them."
						@click="showCanon = !showCanon"
					>
						<span class="tl-switch" aria-hidden="true"><span class="tl-switch-knob"></span></span>
						<span>True canon 5019–5021u</span>
					</button>
					<div class="filter-pills faction-pills">
						<button
							v-for="(faction, key) in factions"
							:key="key"
							class="filter-pill faction-pill"
							:class="{ active: factionFilter === key }"
							:style="pillStyle(key, faction)"
							@click="factionFilter = key"
							@mouseenter="hoveredFaction = key"
							@mouseleave="hoveredFaction = null"
						>
							{{ faction.name }}
						</button>
					</div>
					<label class="search-field tl-search">
						<i class="mdi mdi-magnify search-icon" aria-hidden="true"></i>
						<input
							v-model="searchQuery"
							class="search-input"
							placeholder="Search events..."
							type="text"
						/>
					</label>
					<div class="tl-eras">
						<button
							v-for="m in visibleMarkers"
							:key="m.uuid"
							class="tl-era-chip"
							:class="{ current: currentMarker === m.uuid }"
							@click="jumpTo(m)"
						>
							{{ m.year }}<small>{{ m.era }}</small>
						</button>
					</div>
				</div>

				<!-- Same place and size as the carousel arrows -->
				<button
					class="tl-nav left"
					:disabled="!hasStep(-1)"
					@click="step(-1)"
					aria-label="Previous event"
				>
					<o-icon pack="mdi" icon="chevron-left" size="large" />
				</button>
				<button
					class="tl-nav right"
					:disabled="!hasStep(1)"
					@click="step(1)"
					aria-label="Next event"
				>
					<o-icon pack="mdi" icon="chevron-right" size="large" />
				</button>

				<div
					class="tl-disclaimer"
					v-decrypt="{ after: '#timelineView', delay: 0.1, duration: 1.2 }"
				>
					// History as defined by the Union. Treat with a grain of salt. //
				</div>
			</div>
		</section>
	</div>
</template>

<script>
import timelineData from "@/data/timeline-data.json";
import FlickeringGrid from "@/components/FlickeringGrid.vue";
import { animate, stagger } from "motion";
import { retrieveRows, reducedMotion } from "@/utils/retrieve";
import { onEscape } from "@/utils/escape";

const EASE_OUT = [0.22, 1, 0.36, 1];
// Motion writes its final values once it finishes; clear ours just after that
const later = fn => setTimeout(fn, 50);

// The campaign's present is 5019u; the published Dawnline Incident runs 5019-5021u
const CANON_FROM = 5019;
const CANON_TO = 5021;
// The Incident's opening entry is the campaign's starting point, so it always shows
// The campaign's present: the latest event the party has reached. The timeline opens
// centred on it, and it shows whether or not "True canon" is on.
const PRESENT_EVENT = "51b2bb52-ffff-4daa-a5d1-d745464bffee"; // "Concordant Administration Attacks the Perfect Ministeriat"
const ALWAYS_SHOWN = [
	"51b2bb52-ffff-4daa-a5d1-d745464babee", // "The Dawnline Incident"
	PRESENT_EVENT,
];

export default {
	components: { FlickeringGrid },
	props: {
		animate: {
			type: Boolean,
			required: true,
		},
	},
	data() {
		return {
			animateView: this.animate,
			animationDelay: "0s",
			factions: timelineData.factions,
			sources: timelineData.sources,
			rawTimeline: timelineData.timeline,
			selectedEvent: null,
			showCanon: false,
			eraFilter: "ALL",
			factionFilter: "all-factions",
			searchQuery: "",
			hoveredFaction: null,
			currentMarker: null,
			dragging: false,
			toolbarHeight: 0,
		};
	},
	computed: {
		sortedTimeline() {
			// Chronological: BU counts down to 0, then U counts up
			return [...this.rawTimeline].sort((a, b) => {
				const toAbsolute = e => (e.era === "BU" ? -e.year : e.year);
				const diff = toAbsolute(a) - toAbsolute(b);
				if (diff !== 0) return diff;
				if (a.marker && !b.marker) return -1;
				if (!a.marker && b.marker) return 1;
				return (a.intraYearIndex || 0) - (b.intraYearIndex || 0);
			});
		},
		filteredTimeline() {
			const passes = event => {
				if (!this.showCanon && this.isCanonIncident(event)) return false;
				if (this.eraFilter !== "ALL" && event.era !== this.eraFilter) return false;
				if (this.factionFilter !== "all-factions" && !event.factions.includes(this.factionFilter))
					return false;
				const q = this.searchQuery.trim().toLowerCase();
				if (
					q &&
					!event.title.toLowerCase().includes(q) &&
					!(event.descr || "").toLowerCase().includes(q)
				)
					return false;
				return true;
			};
			// Keep a marker only if at least one event after it (before the next marker) passes
			const keep = new Set();
			let marker = null;
			for (const event of this.sortedTimeline) {
				if (event.marker) marker = event.uuid;
				else if (marker && passes(event)) keep.add(marker);
			}
			return this.sortedTimeline.filter(e => {
				if (e.marker) return keep.has(e.uuid) && (this.showCanon || !this.isCanonIncident(e));
				return passes(e);
			});
		},
		// Events alternate above and below the axis
		laidOut() {
			let n = 0;
			return this.filteredTimeline.map(e =>
				e.marker ? e : { ...e, side: n++ % 2 ? "down" : "up" }
			);
		},
		events() {
			return this.filteredTimeline.filter(e => !e.marker);
		},
		visibleMarkers() {
			return this.filteredTimeline.filter(e => e.marker && /Marker$/.test(e.title));
		},
	},
	watch: {
		// Runs before the DOM updates, so note what's in the middle of the screen now and
		// keep it there afterwards (toggling True canon or a filter shouldn't jump away)
		filteredTimeline() {
			const anchor = this.centredUuid();
			this.$nextTick(() => {
				const track = this.$refs.track;
				const visible = uuid => uuid && this.events.some(e => e.uuid === uuid);
				if (this.selectedEvent && visible(this.selectedEvent.uuid))
					this.centre(this.selectedEvent, false);
				else if (visible(anchor)) this.centre({ uuid: anchor }, false);
				else if (visible(PRESENT_EVENT)) this.centre({ uuid: PRESENT_EVENT }, false);
				else if (track) track.scrollLeft = 0;
				this.onScroll();
				this.buildIn({ base: 0, quick: true });
			});
		},
	},
	mounted() {
		// Cards above the axis must stop short of the floating toolbar
		this.toolbarObserver = new ResizeObserver(
			() => (this.toolbarHeight = this.$refs.toolbar.offsetHeight)
		);
		this.toolbarObserver.observe(this.$refs.toolbar);
		this.$nextTick(() => {
			const track = this.$refs.track;
			const present = this.events.filter(e => e.era === "BU" || e.year <= CANON_FROM);
			const last = this.events.find(e => e.uuid === PRESENT_EVENT) || present[present.length - 1];
			// Start at the present (the campaign's latest event), centred
			if (this.selectedEvent) this.centre(this.selectedEvent, false);
			else if (last && track) {
				const el = track.querySelector(`[data-uuid="${last.uuid}"]`);
				if (el) track.scrollLeft = el.offsetLeft + el.offsetWidth / 2 - track.clientWidth / 2;
			}
			this.onScroll();
			if (this.animate) this.intro();
			// Esc: close the open card, then clear the search
			this.offEscape = onEscape(() => {
				if (this.selectedEvent) return this.clearSelection(), true;
				if (this.searchQuery) return (this.searchQuery = ""), true;
				return false;
			});
		});
	},
	beforeUnmount() {
		this.offEscape?.();
		cancelAnimationFrame(this.followFrame);
		this.toolbarObserver?.disconnect();
		window.removeEventListener("pointermove", this.onPointerMove);
		window.removeEventListener("pointerup", this.onPointerUp);
	},
	methods: {
		// Page entrance: the controls load in, the axis draws outward from the centre of
		// the screen, then the events in view build onto it from the middle outwards
		intro() {
			if (reducedMotion()) return;
			const toolbar = this.$refs.toolbar;
			retrieveRows(
				toolbar.querySelectorAll(".tl-heading, .filter-pills, .tl-canon, .tl-search, .tl-eras"),
				{ startDelay: 0.1, gap: 0.09, duration: 0.5 }
			);
			this.buildIn({ base: 0.35 });
		},
		buildIn({ base = 0, quick = false } = {}) {
			const track = this.$refs.track;
			const axis = this.$refs.axis;
			if (!track || reducedMotion()) return;
			const mid = track.scrollLeft + track.clientWidth / 2;
			// Clear everything Motion left inline (incl. opacity, which would otherwise show
			// the diamond through an open card)
			const done = els =>
				els.forEach(
					el =>
						(el.style.transform =
							el.style.clipPath =
							el.style.transformOrigin =
							el.style.opacity =
								"")
				);
			if (axis && !quick) {
				axis.style.transformOrigin = `${mid}px center`;
				animate(
					axis,
					{ scaleX: [0, 1] },
					{ duration: 1.1, delay: base, ease: EASE_OUT }
				).finished.then(() => later(() => done([axis])));
			}
			// Only what's on screen animates; the rest of the line is already in place
			const inView = [...track.querySelectorAll(".tl-event, .tl-marker")]
				.filter(
					el =>
						el.offsetLeft + el.offsetWidth > track.scrollLeft - 100 &&
						el.offsetLeft < track.scrollLeft + track.clientWidth + 100
				)
				.sort(
					(a, b) =>
						Math.abs(a.offsetLeft + a.offsetWidth / 2 - mid) -
						Math.abs(b.offsetLeft + b.offsetWidth / 2 - mid)
				);
			const step = quick ? 0.03 : 0.08;
			const start = base + (quick ? 0 : 0.45);
			inView.forEach((el, n) => {
				const delay = start + n * step;
				if (el.classList.contains("tl-marker")) {
					animate(el, { opacity: [0, 1] }, { duration: 0.5, delay }).finished.then(() =>
						later(() => (el.style.opacity = ""))
					);
					return;
				}
				const up = el.classList.contains("up");
				const node = el.querySelector(".tl-node");
				const stem = el.querySelector(".tl-stem");
				const card = el.querySelector(".tl-card");
				// The diamond lands on the axis, the stem grows away from it, then the card unrolls
				animate(
					node,
					{ scale: [0, 1], rotate: [45, 45], opacity: [0, 1] },
					{ duration: 0.35, delay, ease: EASE_OUT }
				).finished.then(() => later(() => done([node])));
				if (stem && !el.classList.contains("active")) {
					stem.style.transformOrigin = up ? "bottom" : "top";
					animate(
						stem,
						{ scaleY: [0, 1] },
						{ duration: 0.25, delay: delay + 0.12, ease: "easeOut" }
					).finished.then(() => later(() => done([stem])));
				}
				if (card && !el.classList.contains("active")) {
					const from = up ? "inset(100% 0 0 0)" : "inset(0 0 100% 0)";
					animate(
						card,
						{ clipPath: [from, "inset(0% 0 0% 0)"], opacity: [0, 1] },
						{ duration: quick ? 0.35 : 0.5, delay: delay + 0.2, ease: EASE_OUT }
					).finished.then(() => later(() => (card.style.clipPath = card.style.opacity = "")));
				}
			});
		},
		// Selecting is instant for now: the card opens in place and the line jumps to it
		selectEvent(event) {
			this.transitionTo(event);
		},
		// Opening, switching and closing share one "shared layout" animation (FLIP):
		// note where every column and the affected cards are, jump straight to the final
		// layout (new card open and centred), then animate everything from its old place
		// to its new one with transforms only. Nothing scrolls while it moves.
		transitionTo(next) {
			const track = this.$refs.track;
			const previous = this.selectedEvent;
			if (!track || reducedMotion()) {
				this.selectedEvent = next;
				if (next) this.$nextTick(() => this.centre(next, false));
				return;
			}
			const snapshot = () => {
				const map = new Map();
				track.querySelectorAll(".tl-event, .tl-marker").forEach(el => {
					const card = el.querySelector(".tl-card");
					map.set(el.dataset.uuid, {
						col: el.getBoundingClientRect(),
						card: card?.getBoundingClientRect(),
					});
				});
				return map;
			};
			const before = snapshot();
			this.selectedEvent = next;
			this.$nextTick(() => {
				if (next) this.centre(next, false);
				const after = snapshot();
				const view = track.getBoundingClientRect();
				const width = view.width;
				const timing = { duration: 420, easing: "cubic-bezier(0.22, 1, 0.36, 1)" };
				const changed = new Set([previous?.uuid, next?.uuid].filter(Boolean));
				track.querySelectorAll(".tl-event, .tl-marker").forEach(el => {
					const uuid = el.dataset.uuid;
					const b = before.get(uuid);
					const a = after.get(uuid);
					if (!b || !a) return;
					// Only what ends up on (or near) screen needs to move
					if (a.col.right < view.left - 300 || a.col.left > view.right + 300) return;
					const dx = Math.max(-width, Math.min(width, b.col.left - a.col.left));
					if (Math.abs(dx) > 0.5)
						el.animate([{ transform: `translateX(${dx}px)` }, { transform: "none" }], timing);
					if (!changed.has(uuid) || !b.card || !a.card) return;
					// The connector and diamond follow the card: they retract into the line as a
					// card opens, and grow back out of it as a card closes
					const opening = uuid === next?.uuid;
					const stem = el.querySelector(".tl-stem");
					const node = el.querySelector(".tl-node");
					if (stem) {
						stem.style.transformOrigin = el.classList.contains("up") ? "bottom" : "top";
						const frames = [
							{ transform: "scaleY(1)", opacity: 1 },
							{ transform: "scaleY(0)", opacity: 0 },
						];
						stem
							.animate(opening ? frames : frames.reverse(), {
								duration: opening ? 200 : 260,
								delay: opening ? 0 : 200,
								easing: "ease-out",
								fill: "backwards",
							})
							.finished.then(() => later(() => (stem.style.transformOrigin = "")));
					}
					if (node) {
						const frames = [
							{ transform: "rotate(45deg) scale(1)", opacity: 1 },
							{ transform: "rotate(45deg) scale(0)", opacity: 0 },
						];
						node.animate(opening ? frames : frames.reverse(), {
							duration: 200,
							delay: opening ? 0 : 160,
							easing: "ease-out",
							fill: "backwards",
						});
					}
					// The card that opened or closed morphs between its small and large box
					const card = el.querySelector(".tl-card");
					const cdx = b.card.left + b.card.width / 2 - (a.card.left + a.card.width / 2) - dx;
					const cdy = b.card.top + b.card.height / 2 - (a.card.top + a.card.height / 2);
					const sx = b.card.width / a.card.width;
					const sy = b.card.height / a.card.height;
					card.animate(
						[
							{ transform: `translate(${cdx}px, ${cdy}px) scale(${sx}, ${sy})` },
							{ transform: "none" },
						],
						timing
					);
					// Its contents would stretch mid-morph, so they fade in as it settles
					[...card.children].forEach(child =>
						child.animate([{ opacity: 0 }, { opacity: 1 }], {
							duration: 200,
							delay: 170,
							fill: "backwards",
						})
					);
				});
			});
		},
		// The event column closest to the middle of the screen
		centredUuid() {
			const track = this.$refs.track;
			if (!track) return null;
			const mid = track.scrollLeft + track.clientWidth / 2;
			let best = null;
			let gap = Infinity;
			track.querySelectorAll(".tl-event").forEach(el => {
				const d = Math.abs(el.offsetLeft + el.offsetWidth / 2 - mid);
				if (d < gap) (gap = d), (best = el.dataset.uuid);
			});
			return best;
		},
		// Canon events of the Dawnline Incident, which the campaign is playing through
		isCanonIncident(event) {
			if (ALWAYS_SHOWN.includes(event.uuid)) return false;
			return event.era === "U" && event.year >= CANON_FROM && event.year <= CANON_TO;
		},
		clearSelection() {
			if (this.dragMoved || !this.selectedEvent) return;
			this.transitionTo(null);
		},
		pillStyle(key, faction) {
			const on = this.factionFilter === key;
			const hover = this.hoveredFaction === key;
			return {
				borderColor: on ? faction.color : faction.color + "80",
				color: this.readable(faction.color),
				background: on ? faction.color + "2e" : hover ? faction.color + "1a" : "transparent",
			};
		},
		// Faction colours lifted towards white so they read as text on the dark panel
		readable(color) {
			return `color-mix(in srgb, ${color} 62%, #ffffff)`;
		},
		isSelected(event) {
			return !!this.selectedEvent && this.selectedEvent.uuid === event.uuid;
		},
		// Only the card and its diamond open an event; the empty space in its column
		// behaves like the rest of the line (closes whatever is open)
		clickEvent(event, e) {
			if (this.dragMoved) return;
			if (!e.target.closest(".tl-card, .tl-node")) return this.clearSelection();
			// Clicking the open card again closes it
			if (this.isSelected(event)) this.clearSelection();
			else this.selectEvent(event);
		},
		centre(event, smooth) {
			const track = this.$refs.track;
			const el = track && track.querySelector(`[data-uuid="${event.uuid}"]`);
			if (!el) return;
			// The selected column widens over ~0.5s and pushes its neighbours away, so
			// keep easing towards its (moving) centre until the layout settles
			const target = () => el.offsetLeft + el.offsetWidth / 2 - track.clientWidth / 2;
			cancelAnimationFrame(this.followFrame);
			if (!smooth) {
				track.scrollLeft = target();
				return;
			}
			const start = performance.now();
			const tick = now => {
				const done = now - start > 480;
				track.scrollLeft += (target() - track.scrollLeft) * (done ? 1 : 0.3);
				if (!done) this.followFrame = requestAnimationFrame(tick);
			};
			this.followFrame = requestAnimationFrame(tick);
		},
		jumpTo(marker) {
			const track = this.$refs.track;
			const el = track && track.querySelector(`[data-uuid="${marker.uuid}"]`);
			if (el) track.scrollTo({ left: el.offsetLeft - 80, behavior: "smooth" });
		},
		indexOfSelected() {
			return this.selectedEvent
				? this.events.findIndex(e => e.uuid === this.selectedEvent.uuid)
				: -1;
		},
		hasStep(dir) {
			const i = this.indexOfSelected();
			if (i === -1) return this.events.length > 0;
			return !!this.events[i + dir];
		},
		step(dir) {
			const i = this.indexOfSelected();
			const next = i === -1 ? this.events[0] : this.events[i + dir];
			if (next) this.selectEvent(next);
		},
		// Vertical mouse wheels scroll the track sideways
		onWheel(e) {
			if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
				this.$refs.track.scrollLeft += e.deltaY;
				e.preventDefault();
			}
		},
		onScroll() {
			const track = this.$refs.track;
			if (!track) return;
			const probe = track.scrollLeft + track.clientWidth / 3;
			let current = null;
			for (const m of this.visibleMarkers) {
				const el = track.querySelector(`[data-uuid="${m.uuid}"]`);
				if (el && el.offsetLeft <= probe) current = m.uuid;
			}
			this.currentMarker = current || (this.visibleMarkers[0] && this.visibleMarkers[0].uuid);
		},
		// Click-and-drag to pan
		onPointerDown(e) {
			if (e.button !== 0 || e.pointerType === "touch") return;
			this.dragStart = { x: e.clientX, left: this.$refs.track.scrollLeft };
			this.dragMoved = false;
			window.addEventListener("pointermove", this.onPointerMove);
			window.addEventListener("pointerup", this.onPointerUp);
		},
		onPointerMove(e) {
			const dx = e.clientX - this.dragStart.x;
			if (!this.dragMoved && Math.abs(dx) > 5) {
				this.dragMoved = true;
				this.dragging = true;
			}
			if (this.dragMoved) this.$refs.track.scrollLeft = this.dragStart.left - dx;
		},
		onPointerUp() {
			window.removeEventListener("pointermove", this.onPointerMove);
			window.removeEventListener("pointerup", this.onPointerUp);
			this.dragging = false;
			// Let the click that ends a drag be ignored, then reset
			setTimeout(() => (this.dragMoved = false));
		},
		// Equal colour bands, one per faction ("No Faction" only when it's alone)
		splitGradient(event) {
			const keys = this.sortedFactions(event.factions);
			const real = keys.filter(k => k !== "no-faction");
			const colors = (real.length ? real : keys).map(k => this.getFactionColor(k));
			if (colors.length === 1) return `linear-gradient(90deg, ${colors[0]}, ${colors[0]})`;
			const step = 100 / colors.length;
			return `linear-gradient(90deg, ${colors
				.map((c, i) => `${c} ${(i * step).toFixed(2)}% ${((i + 1) * step).toFixed(2)}%`)
				.join(", ")})`;
		},
		primaryFaction(event) {
			return this.sortedFactions(event.factions).find(f => f !== "no-faction") || event.factions[0];
		},
		sortedFactions(factionKeys) {
			const order = Object.keys(this.factions);
			return [...factionKeys].sort((a, b) => order.indexOf(a) - order.indexOf(b));
		},
		getFactionColor(key) {
			return this.factions[key]?.color || "#888";
		},
		getFactionName(key) {
			return this.factions[key]?.name || key;
		},
		getSourceName(key) {
			return this.sources[key]?.name || key;
		},
		getSourceUrl(key) {
			return this.sources[key]?.url || "#";
		},
		validSources(event) {
			return (event.sources || []).filter(s => s.sourceKey && s.sourceKey !== "no-source");
		},
	},
};
</script>

<style scoped>
/* ── Stage: full-bleed gunmetal panel, like the carousel pages ── */
.tl-stage {
	--strip-h: 80px; /* height of .carousel-roster; keeps the axis level with the carousel arrows */
	position: relative;
	height: 100%;
	overflow: hidden;
	outline: none;
	background-color: var(--secondary-color);
}

.tl-bg {
	z-index: 0;
	-webkit-mask-image: radial-gradient(ellipse at 50% 45%, #000 30%, transparent 85%);
	mask-image: radial-gradient(ellipse at 50% 45%, #000 30%, transparent 85%);
}

/* ── Toolbar (floats over the top of the track) ── */
.tl-toolbar {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	z-index: 4;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.7em 1.4em;
	padding: 1em 1.5em 0.6em;
	pointer-events: none;
}

.tl-toolbar > * {
	pointer-events: auto;
}

.tl-heading {
	font-family: "Big Shoulders Display", cursive;
	font-size: 24px;
	font-weight: 800;
	letter-spacing: 0.12em;
	color: var(--text-mission-status);
}

.tl-toolbar .filter-pills {
	gap: 0.4em;
}

.tl-toolbar .filter-pill {
	padding: 0.45em 0.95em;
	font-size: 11px;
}

.tl-search {
	margin-left: auto;
	width: 320px;
}

/* True canon: an angular (skewed) pill switch */
.tl-canon {
	display: inline-flex;
	align-items: center;
	gap: 0.7em;
}

.tl-switch {
	position: relative;
	flex-shrink: 0;
	width: 32px;
	height: 14px;
	margin-left: 3px;
	border: 1px solid rgba(209, 200, 184, 0.4);
	background: rgba(26, 26, 31, 0.6);
	transform: skewX(-20deg);
	transition: background-color 0.2s, border-color 0.2s;
}

.tl-switch-knob {
	position: absolute;
	top: 2px;
	left: 2px;
	width: 12px;
	height: 8px;
	background: rgba(209, 200, 184, 0.55);
	transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.2s, box-shadow 0.2s;
}

.tl-canon.active .tl-switch {
	border-color: #b8973a;
	background: rgba(184, 151, 58, 0.2);
}

.tl-canon.active .tl-switch-knob {
	transform: translateX(14px);
	background: #b8973a;
	box-shadow: 0 0 6px rgba(184, 151, 58, 0.7);
}

/* Row 1: title, eras, search. Row 2: factions. Row 3: era jump */
.tl-toolbar .faction-pills {
	order: 3;
	flex-basis: 100%;
}

.tl-toolbar .tl-eras {
	order: 4;
	flex-basis: 100%;
}

.tl-search .search-input {
	height: 36px;
	padding-left: 2.4em;
	font-size: 13px;
}

.tl-search :deep(.search-icon),
.tl-search .search-icon {
	font-size: 18px;
}

/* ── Track ── */
.tl-track {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: var(--strip-h);
	z-index: 1;
	overflow-x: auto;
	overflow-y: hidden;
	cursor: grab;
	scrollbar-width: none;
	-webkit-mask-image: linear-gradient(90deg, transparent, #000 4%, #000 96%, transparent);
	mask-image: linear-gradient(90deg, transparent, #000 4%, #000 96%, transparent);
}

.tl-track::-webkit-scrollbar {
	display: none;
}

.tl-track.dragging {
	cursor: grabbing;
	user-select: none;
}

.tl-rail {
	position: relative;
	display: flex;
	height: 100%;
	width: max-content;
	min-width: 100%;
	padding: 0 50vw 0 8vw;
}

/* The axis */
.tl-axis {
	position: absolute;
	left: 0;
	right: 0;
	top: 50%;
	height: 2px;
	margin-top: -1px;
	background: linear-gradient(
		90deg,
		rgba(122, 28, 46, 0.2),
		var(--primary-color) 6%,
		var(--primary-color) 94%,
		rgba(122, 28, 46, 0.2)
	);
	pointer-events: none;
}

/* Era markers: wide enough that no neighbouring card reaches the dashed rule */
.tl-marker {
	position: relative;
	flex: 0 0 210px;
	display: flex;
	justify-content: center;
}

.tl-marker::before {
	content: "";
	position: absolute;
	top: calc(var(--toolbar-h) + 10px);
	bottom: 14px;
	left: 50%;
	border-left: 1px dashed rgba(184, 151, 58, 0.45);
}

.tl-marker-label {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	padding: 0.15em 0.55em;
	background: #26242f;
	border: 1px solid rgba(184, 151, 58, 0.6);
	font-family: "Big Shoulders Display", cursive;
	font-size: 20px;
	font-weight: 800;
	line-height: 1;
	letter-spacing: 0.06em;
	color: #b8973a;
	white-space: nowrap;
}

/* Events: a node on the axis, a stem, and a card above or below */
.tl-event {
	position: relative;
	flex: 0 0 150px;
	cursor: grab;
}

.tl-card,
.tl-node {
	cursor: pointer;
}

.tl-node {
	position: absolute;
	top: 50%;
	left: 50%;
	width: 11px;
	height: 11px;
	margin: -5.5px 0 0 -5.5px;
	border: 2px solid var(--c);
	background: #1a1a1f;
	transform: rotate(45deg);
	transition: transform 0.2s, background-color 0.2s, box-shadow 0.2s;
}

.tl-stem {
	position: absolute;
	left: 50%;
	width: 1px;
	height: 26px;
	background: rgba(209, 200, 184, 0.25);
	transition: background-color 0.2s;
}

.tl-event.up .tl-stem {
	bottom: calc(50% + 6px);
}
.tl-event.down .tl-stem {
	top: calc(50% + 6px);
}

.tl-card {
	position: absolute;
	left: 50%;
	width: 280px;
	/* Centred with a margin, not translateX(-50%): keeps text crisp and lets the
	   open/close animation use transform freely */
	margin-left: -140px;
	display: flex;
	flex-direction: column;
	gap: 0.25em;
	padding: 0.6em 0.8em;
	border: 1px solid rgba(209, 200, 184, 0.14);
	border-top: 0;
	padding-top: calc(0.6em + 2px);
	background: var(--split) top / 100% 2px no-repeat, rgba(26, 26, 31, 0.85);
	overflow-y: auto;
	scrollbar-width: thin;
	transition: border-color 0.2s, background-color 0.2s;
}

.tl-event.up .tl-card {
	bottom: calc(50% + 32px);
	max-height: calc(50% - 32px - var(--toolbar-h) - 8px);
}

.tl-event.down .tl-card {
	top: calc(50% + 32px);
	max-height: calc(50% - 32px - 10px);
}

/* Fixed-height cards scroll instead of squashing their contents */
.tl-card > * {
	flex-shrink: 0;
}

.tl-card-year {
	font-family: "Big Shoulders Display", cursive;
	font-size: 17px;
	font-weight: 800;
	line-height: 1;
	letter-spacing: 0.06em;
	color: #b8973a;
}

.tl-marker-label small,
.tl-card-year small,
.tl-era-chip small {
	margin-left: 0.15em;
	font-size: 0.65em;
}

.tl-card-title {
	font-family: "Titillium Web", sans-serif;
	font-size: 13px;
	font-weight: 600;
	line-height: 1.3;
	color: #d1c8b8;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.tl-card-descr {
	font-family: "Titillium Web", sans-serif;
	font-size: 12px;
	line-height: 1.4;
	color: rgba(209, 200, 184, 0.6);
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.tl-card-dots {
	display: flex;
	gap: 4px;
	margin-top: 0.1em;
}

.faction-dot {
	width: 7px;
	height: 7px;
	border-radius: 50%;
}

/* Hover only when the pointer is on the card or its diamond, not the empty column */
.tl-event:has(.tl-card:hover, .tl-node:hover) .tl-card {
	border-color: color-mix(in srgb, var(--c) 55%, transparent);
}

.tl-event:has(.tl-card:hover, .tl-node:hover) .tl-node {
	transform: rotate(45deg) scale(1.35);
	background: var(--c);
}

/* ── Selected event: its column widens (pushing the neighbours aside) and the
   card moves onto the axis, centred on screen ── */
.tl-event.active {
	flex-basis: 700px;
	z-index: 3;
	cursor: default;
}

.tl-event.active.up .tl-card,
.tl-event.active.down .tl-card {
	/* Centred with margins rather than translate(-50%, -50%), which can land text on a
	   half pixel and blur it */
	top: 0;
	bottom: 0;
	margin: auto 0 auto -270px;
	height: fit-content;
	width: 540px;
	max-height: calc(100% - 2 * (var(--toolbar-h) + 14px));
	transform: none;
	gap: 0.55em;
	padding: 1.1em 1.4em 1.2em;
	background: rgba(30, 27, 32, 0.97);
	border: 2px solid var(--c);
	border-top-width: 4px;
	border-image: var(--split) 1;
	box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.4), 0 0 24px color-mix(in srgb, var(--c) 30%, transparent),
		0 18px 50px rgba(0, 0, 0, 0.55);
}

.tl-event.active .tl-node,
.tl-event.active .tl-stem {
	opacity: 0;
}

.tl-card-head {
	display: flex;
	align-items: center;
	gap: 0.8em;
	flex-wrap: wrap;
}

.tl-event.active .tl-card-year {
	font-size: 34px;
}

.tl-event.active .tl-card-title {
	display: block;
	font-family: "Big Shoulders Display", cursive;
	font-size: 26px;
	font-weight: 800;
	line-height: 1.1;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	color: #fff;
}

.tl-event.active .tl-card-full {
	font-size: 14px;
	line-height: 1.6;
}

.tl-card-factions {
	display: flex;
	flex-wrap: wrap;
	gap: 0.3em;
}

.faction-tag {
	padding: 0.2em 0.65em;
	border: 1px solid;
	font-family: "Share Tech Mono", monospace;
	font-size: 12px;
	letter-spacing: 0.08em;
	text-transform: uppercase;
}

.tl-card-full {
	margin: 0;
	font-family: "Titillium Web", sans-serif;
	font-size: 13px;
	line-height: 1.55;
	color: var(--text-markdown-p);
}

.tl-card-sources {
	display: flex;
	flex-wrap: wrap;
	gap: 0.2em 0.9em;
	padding-top: 0.4em;
	border-top: 1px solid rgba(209, 200, 184, 0.12);
}

.source-entry {
	display: inline-flex;
	gap: 0.35em;
	font-family: "Titillium Web", sans-serif;
	font-size: 11px;
}

.source-link {
	color: #b8973a;
	text-decoration: none;
}

.source-link:hover {
	text-decoration: underline;
}

.source-name {
	color: #d1c8b8;
}

.source-location {
	color: rgba(209, 200, 184, 0.45);
}

.tl-empty {
	align-self: center;
	font-family: "Share Tech Mono", monospace;
	font-size: 11px;
	letter-spacing: 0.1em;
	color: var(--text-pilot-code);
	opacity: 0.5;
}

/* ── Arrows: same spot and size as .carousel-arrow (1em in, level with the axis) ── */
.tl-nav {
	position: absolute;
	top: calc((100% - var(--strip-h)) / 2);
	z-index: 5;
	transform: translateY(-50%);
	display: flex;
	padding: 0;
	background: none;
	border: none;
	color: #fff;
	filter: drop-shadow(0 0 6px rgba(0, 0, 0, 0.9));
	opacity: 0.85;
	cursor: pointer;
	transition: opacity 0.15s;
}

.tl-nav.left {
	left: 1em;
}
.tl-nav.right {
	right: 1em;
}
.tl-nav:hover {
	opacity: 1;
}
.tl-nav:disabled {
	opacity: 0.2;
	cursor: default;
}

/* ── Era jump row ── */
.tl-eras {
	display: flex;
	align-items: center;
	gap: 0.4em;
	min-width: 0;
	overflow-x: auto;
	scrollbar-width: none;
}

.tl-era-chip {
	flex-shrink: 0;
	padding: 0.15em 0.7em;
	border: 1px solid rgba(209, 200, 184, 0.2);
	background: transparent;
	font-family: "Big Shoulders Display", cursive;
	font-size: 16px;
	font-weight: 800;
	letter-spacing: 0.08em;
	color: rgba(209, 200, 184, 0.6);
	cursor: pointer;
	transition: color 0.15s, border-color 0.15s, background-color 0.15s;
}

.tl-era-chip:hover {
	color: #fff;
	border-color: rgba(184, 151, 58, 0.6);
}

.tl-era-chip.current {
	color: #b8973a;
	border-color: #b8973a;
	background: rgba(184, 151, 58, 0.1);
}

.tl-disclaimer {
	position: absolute;
	left: 1.5em;
	bottom: 1.2em;
	z-index: 4;
	font-family: "Share Tech Mono", monospace;
	font-size: 10px;
	letter-spacing: 0.15em;
	text-transform: uppercase;
	color: rgba(184, 151, 58, 0.6);
}

/* The page entrance is driven by Motion (intro / buildIn) */
.timeline-section.animate {
	animation: tlFade 0.4s ease-out both;
}

@keyframes tlFade {
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
}

@media (max-width: 768px) {
	.tl-search {
		margin-left: 0;
		width: 100%;
	}
}
</style>
